import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    getPublicCourse,
    joinCourse,
    addPendingScore,
    courseErrorMessage,
} from "../courses";
import { useAuth } from "../contexts/Auth";
import PracticeSentenceModal from "../components/Dashboard/PracticeSentenceModal";

const scoreBadgeClass = (score) => {
    if (score === null || score === undefined) return "bg-gray-100 text-gray-500";
    if (score >= 85) return "bg-green-100 text-green-800";
    if (score >= 65) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
};

// Mean of the non-null part scores, matching backend progress.py:_combined_score.
const combinedScore = (scores) => {
    const parts = ["accuracy_score", "completeness_score", "fluency_score"]
        .map((key) => scores?.[key])
        .filter((value) => value !== null && value !== undefined);
    if (!parts.length) return null;
    return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);
};

// Logged-out view of a shared course (/c/<join_code>): see the sentences and
// practice them with no account. Scores stay in the session and are only saved
// once the visitor signs up (see addPendingScore / claimPendingScores).
const PublicCoursePage = () => {
    const { code } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated, loading: authLoading } = useAuth();

    const [course, setCourse] = useState(null);
    const [error, setError] = useState(null);
    const [practiceSentence, setPracticeSentence] = useState(null);
    // Best combined score per sentence text, for the badge display.
    const [bestScores, setBestScores] = useState({});
    // Sentence texts the visitor has produced at least one result for.
    const [practiced, setPracticed] = useState(() => new Set());

    // Logged-in users don't need the public view: auto-join and send them to
    // the real course page (teachers to their own teach page).
    useEffect(() => {
        if (authLoading || !isAuthenticated) return;
        let cancelled = false;
        joinCourse(code)
            .then((data) => {
                if (data.language) localStorage.setItem("lastSelectedLanguage", data.language);
                if (!cancelled) navigate(`/courses/${data.id}`, { replace: true });
            })
            .catch((err) => {
                if (cancelled) return;
                if (err.code === "self_join") {
                    // Teacher viewing their own course; needs the numeric id.
                    getPublicCourse(code)
                        .then((c) => navigate(`/teach/${c.id}`, { replace: true }))
                        .catch((e) => setError(courseErrorMessage(e, t)));
                } else {
                    setError(courseErrorMessage(err, t));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [authLoading, isAuthenticated, code, navigate, t]);

    // Load the public content for logged-out visitors.
    useEffect(() => {
        if (authLoading || isAuthenticated) return;
        getPublicCourse(code)
            .then((c) => {
                setCourse(c);
                // Pre-select this course's language so the dashboard the student
                // lands on after signing up through the class link already shows
                // it (LanguageSelect reads this key). Same codes on both sides.
                if (c.language) localStorage.setItem("lastSelectedLanguage", c.language);
            })
            .catch((err) => setError(courseErrorMessage(err, t)));
    }, [authLoading, isAuthenticated, code, t]);

    const handleResult = useCallback(
        ({ sentence, shareCode, scores }) => {
            addPendingScore(code, shareCode);
            setPracticed((prev) => new Set(prev).add(sentence));
            const combined = combinedScore(scores);
            if (combined !== null) {
                setBestScores((prev) => ({
                    ...prev,
                    [sentence]:
                        prev[sentence] != null
                            ? Math.max(prev[sentence], combined)
                            : combined,
                }));
            }
        },
        [code]
    );

    if (error) {
        return (
            <div className="px-4 pt-8 md:px-20 text-center">
                <p className="text-red-600">{error}</p>
                <Link to="/" className="mt-4 inline-block text-primary underline">
                    {t("appName")}
                </Link>
            </div>
        );
    }

    if (!course) {
        return (
            <div className="px-4 pt-8 md:px-20">
                <div className="h-8 w-1/2 animate-pulse rounded bg-gray-200" />
                <div className="mt-6 h-32 animate-pulse rounded bg-gray-200" />
            </div>
        );
    }

    const sections = [
        ...course.folders.map((folder) => ({
            key: `folder-${folder.id}`,
            name: folder.name,
            sentences: folder.sentences,
        })),
        ...(course.unfiled_sentences.length
            ? [{ key: "unfiled", name: t("courses.unfiled"), sentences: course.unfiled_sentences }]
            : []),
    ];

    // All sentence texts in display order; the practice modal walks this list.
    const orderedTexts = sections.flatMap((section) =>
        section.sentences.map((sentence) => sentence.text)
    );
    const totalSentences = orderedTexts.length;
    const practicedCount = orderedTexts.filter((text) => practiced.has(text)).length;
    const percent = totalSentences ? Math.round((practicedCount / totalSentences) * 100) : 0;
    const allDone = totalSentences > 0 && practicedCount === totalSentences;

    const handleNextSentence = () => {
        const index = orderedTexts.indexOf(practiceSentence);
        setPracticeSentence(orderedTexts[(index + 1) % orderedTexts.length]);
        return Promise.resolve();
    };

    const renderSentence = (sentence) => {
        const best = bestScores[sentence.text];
        return (
            <li key={sentence.id}>
                <button
                    className="w-full text-left rounded-md border border-border bg-white p-3 hover:bg-gray-50 transition-colors flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                    onClick={() => setPracticeSentence(sentence.text)}
                >
                    <span className="min-w-0">{sentence.text}</span>
                    <span className="flex items-center gap-2 shrink-0 text-xs">
                        <span
                            className={`rounded px-2 py-0.5 font-medium ${scoreBadgeClass(best)}`}
                        >
                            {best !== null && best !== undefined
                                ? `${best} ${t("unit.points")}`
                                : t("courses.notPracticed")}
                        </span>
                    </span>
                </button>
            </li>
        );
    };

    return (
        <div className="px-4 pt-4 md:px-20 md:pt-10 flex flex-col gap-4 pb-10">
            <div>
                <h1 className="text-2xl md:text-3xl font-semibold">{course.title}</h1>
                <p className="text-sm text-gray-600">
                    {t("courses.taughtBy")} {course.teacher_email}
                </p>
                <p className="mt-1 text-sm text-gray-500">{t("courses.public.subtitle")}</p>
            </div>

            {/* Progress summary */}
            <div className="rounded-lg border border-border bg-secondary p-4 md:p-6">
                <div className="flex items-center justify-between flex-wrap gap-2 text-sm text-gray-700">
                    <span>
                        {practicedCount}/{totalSentences} {t("courses.sentencesPracticed")}
                    </span>
                </div>
                <div className="mt-2 h-2.5 w-full rounded-full bg-gray-200">
                    <div
                        className="h-2.5 rounded-full bg-primary"
                        style={{ width: `${percent}%` }}
                    />
                </div>
            </div>

            {/* End-of-lection sign-up call to action */}
            {allDone && (
                <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 md:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="font-medium">{t("courses.public.allDoneTitle")}</p>
                        <p className="text-sm text-gray-600">
                            {t("courses.public.saveScoresHint", { count: practicedCount })}
                        </p>
                    </div>
                    <button
                        className="shrink-0 rounded-md bg-primary px-4 py-2 text-white hover:bg-purple-600"
                        onClick={() => navigate("/register")}
                    >
                        {t("courses.public.saveScoresCta")}
                    </button>
                </div>
            )}

            {sections.length === 0 && (
                <p className="text-gray-600">{t("courses.noSentencesYet")}</p>
            )}

            {sections.map((section) => (
                <div
                    key={section.key}
                    className="rounded-lg border border-border bg-secondary p-4 md:p-6"
                >
                    <h2 className="font-medium">{section.name}</h2>
                    {section.sentences.length === 0 ? (
                        <p className="mt-2 text-sm text-gray-500">{t("courses.emptyFolder")}</p>
                    ) : (
                        <ul className="mt-3 flex flex-col gap-2">
                            {section.sentences.map(renderSentence)}
                        </ul>
                    )}
                </div>
            ))}

            {practiceSentence && (
                <PracticeSentenceModal
                    key={practiceSentence}
                    sentence={practiceSentence}
                    selectedLanguage={course.language}
                    onNextSentence={handleNextSentence}
                    onResult={handleResult}
                    allPracticed={allDone}
                    onClose={() => setPracticeSentence(null)}
                />
            )}
        </div>
    );
};

export default PublicCoursePage;
